import type { RegionBorders } from '@tsagko/label-contracts'

/**
 * A single border line in absolute mm. `axis: 'vertical'` draws DOWN from
 * (xMm, yMm) for `lengthMm`; `axis: 'horizontal'` draws RIGHT. The cross-axis
 * size is `thicknessMm`. Shared by the designer canvas and the ZPL emitter so
 * both render region borders from one source of truth.
 */
export interface BorderSegment {
  axis: 'horizontal' | 'vertical'
  xMm: number
  yMm: number
  lengthMm: number
  thicknessMm: number
  style: 'solid' | 'dashed' | 'dotted'
}

/** Leaf box + its region borders. Both `FlatZone` (pipeline) and `LaidOutZone`
 *  (canvas) adapt to this shape at the call site. */
export interface BorderLeaf {
  xMm: number
  yMm: number
  widthMm: number
  heightMm: number
  borders?: RegionBorders
}

/** Tolerance for treating two edges as the same line. Sibling boxes computed
 *  from the same split share bit-identical boundary floats; the epsilon covers
 *  accumulated arithmetic across deeper trees. */
const EPSILON_MM = 1e-6

const STYLE_RANK: Record<BorderSegment['style'], number> = { solid: 3, dashed: 2, dotted: 1 }

function near(a: number, b: number): boolean {
  return Math.abs(a - b) < EPSILON_MM
}

/** One segment per enabled edge, clipped to the leaf's own box. */
export function collectRegionBorders(leaves: BorderLeaf[]): BorderSegment[] {
  const out: BorderSegment[] = []
  for (const { xMm, yMm, widthMm, heightMm, borders } of leaves) {
    if (!borders) continue
    if (borders.top) {
      out.push({ axis: 'horizontal', xMm, yMm, lengthMm: widthMm, thicknessMm: borders.top.thicknessMm, style: borders.top.style })
    }
    if (borders.bottom) {
      out.push({ axis: 'horizontal', xMm, yMm: yMm + heightMm, lengthMm: widthMm, thicknessMm: borders.bottom.thicknessMm, style: borders.bottom.style })
    }
    if (borders.left) {
      out.push({ axis: 'vertical', xMm, yMm, lengthMm: heightMm, thicknessMm: borders.left.thicknessMm, style: borders.left.style })
    }
    if (borders.right) {
      out.push({ axis: 'vertical', xMm: xMm + widthMm, yMm, lengthMm: heightMm, thicknessMm: borders.right.thicknessMm, style: borders.right.style })
    }
  }
  return out
}

function coincident(a: BorderSegment, b: BorderSegment): boolean {
  return a.axis === b.axis && near(a.xMm, b.xMm) && near(a.yMm, b.yMm) && near(a.lengthMm, b.lengthMm)
}

function heavier(a: BorderSegment, b: BorderSegment): BorderSegment {
  if (!near(a.thicknessMm, b.thicknessMm)) return a.thicknessMm > b.thicknessMm ? a : b
  return STYLE_RANK[a.style] >= STYLE_RANK[b.style] ? a : b
}

/** Collapse exactly-coincident segments (heavier wins). Partial overlaps are
 *  left as separate segments — they overlay on the shared portion, which is
 *  idempotent for solid `^GB` output. */
export function dedupeBorderSegments(segments: BorderSegment[]): BorderSegment[] {
  const kept: BorderSegment[] = []
  for (const seg of segments) {
    const i = kept.findIndex(k => coincident(k, seg))
    if (i === -1) kept.push(seg)
    else kept[i] = heavier(kept[i]!, seg)
  }
  return kept
}
